import { useCallback, useEffect, useMemo, useState } from 'react';
import { globalSearchEntityPickerTreeModel } from 'components/EntityPicker/globalSearchEntityPickerTreeModel';
import { dropdownTreeModel } from 'components/EntityPicker/dropdownTreeModel';
import { entityPickerFetcher, IEntityPickerFetcher } from 'components/EntityPicker/entityPickerFetcher';
import { IEntityPickerTreeModel } from 'components/EntityPicker/defaultEpickTreeModel';
import { ComboBoxTree, IComboBoxTreeProps } from 'components/Tree/ComboBoxTree/ComboBoxTree';
import { getDecisionsTypeById, getTypeDisplayName } from 'utils/DecisionsTypeUtils';
import { DecisionsType } from '@decisions/server/DecisionsFramework/Design/Flow/Mapping';
import { IEpickNode } from 'components/EntityPicker/EpickTree';
import { TypePickerDialog } from 'components/Editors/TypePickerDialog';
import { HasOpenState } from 'hooks/HasOpenState';

interface ITypeComboPickerProps extends Omit<IComboBoxTreeProps, 'createModel' | 'searchValue' | 'onSelected' | 'moreItems'> {
  pickedEntity?: DecisionsType | string | null;
  onSelected: (item: DecisionsType | null) => void;
}

// From legacy implementation.
// We need to put moreItems in the end of th list of root nodes.
// So we use 9 number to be sure moreItems after the rest.
export const MORE_ITEMS_START_INDEX = 9;

/**
 * Copyright (c) 2021 Decisions All Rights Reserved
 */
export function TypeComboPicker({ settings, pickedEntity, onSelected, ...props }: ITypeComboPickerProps) {
  const [searchValue, setSearchValue] = useState<string>('');
  const { isOpen, setClosed, setOpen } = HasOpenState(false);

  const moreItems = useMemo(
    () => [
      {
        id: 'open-pick-dialog',
        title: 'All/Search...',
        onSelected: () => setOpen(),
      },
    ],
    [setOpen]
  );

  const updateSearchValue = useCallback(async (type: DecisionsType | null) => {
    if (type) {
      setSearchValue(await getTypeDisplayName(type));
    } else {
      setSearchValue('');
    }
  }, []);

  useEffect(() => {
    if (pickedEntity) {
      if (typeof pickedEntity === 'string') {
        getDecisionsTypeById(pickedEntity).then(updateSearchValue);
      } else if (pickedEntity && typeof pickedEntity === 'object') {
        updateSearchValue(pickedEntity);
      }
    } else {
      updateSearchValue(null);
    }
  }, [pickedEntity, updateSearchValue]);

  const handleSelected = useCallback(
    async (item: IEpickNode | DecisionsType | null) => {
      let type: DecisionsType | null = null;

      if(item && 'Name' in item) {
        type = item;
      } else if (item) {
        type = await getDecisionsTypeById(item.id);
      }

      updateSearchValue(type);
      onSelected?.(type);
    },
    [onSelected, updateSearchValue]
  );

  const handleCreateModel = useCallback(
    (searchValue?: string) => {
      if (!settings) {
        return null;
      }

      const fetcher = entityPickerFetcher({
        filterNames: settings.FilterNames,
        types: settings.Types,
      }) as IEntityPickerFetcher;

      const model = searchValue
        ? globalSearchEntityPickerTreeModel(searchValue, settings, fetcher)
        : dropdownTreeModel(
          settings,
          fetcher,
          moreItems?.map((item, index) => ({
            id: item.id,
            name: item.title,
            pickable: true,
            order: index + MORE_ITEMS_START_INDEX,
            canBeFav: false,
          }))
        );
      return model as IEntityPickerTreeModel;
    },
    [settings, moreItems]
  );

  const onSearch = useCallback((search: string) => {
    setSearchValue(search);
  }, []);

  return (
    <>
      <ComboBoxTree
        searchValue={searchValue}
        onSelected={handleSelected}
        createModel={handleCreateModel}
        onSearch={onSearch}
        moreItems={moreItems}
        callOutHeight={342}
        settings={settings}
        {...props}
      />
      {settings && <TypePickerDialog open={isOpen} settings={settings} setClosed={setClosed} onSelected={handleSelected} />}
    </>
  );
}
